import {
  asFormattedString,
  truncatedZeroPencePrice,
} from "@at/formatter-utils/lib/number-utils/number.utils";
import { pluralise } from "@at/formatter-utils/lib/string-utils/string.utils";
import { Channel } from "@at/sauron-platform-core";

import { VatStatus } from "../../__generated__/graphql-types";
import { FPADataQuery_search_Search_advert_Advert } from "../../connected-components/hoc/query/__generated__/fpa-page-data.query";
import { isTruckPlantFarm } from "../channel/channel-utils";

export const getFormattedDistance = (
  value?: number | null,
  unit?: string | null,
): string | null => {
  if (!value || !unit) {
    return null;
  }

  const formattedValue = asFormattedString(value);

  if (/^(m|miles?)$/i.test(unit)) {
    return `${formattedValue} ${pluralise("mile", value)}`;
  }

  if (/^(km|kilometers?|kilometres?)$/i.test(unit)) {
    return `${formattedValue} ${pluralise("kilometre", value)}`;
  }

  return null;
};

const getAdvertPrice = (
  advert: FPADataQuery_search_Search_advert_Advert,
  channel: Channel,
) => {
  return !!advert.suppliedPrice && channel !== Channel.cars
    ? advert.suppliedPrice
    : advert.price;
};

const getPriceFormatted = (price: number | null | undefined) => {
  return price ? truncatedZeroPencePrice(price) : undefined;
};

const getVatStatus = (
  advert: FPADataQuery_search_Search_advert_Advert,
  channel: Channel,
) => {
  switch (advert.vatStatus) {
    case VatStatus.PLUS_VAT: {
      return " +VAT";
    }
    case VatStatus.INC_VAT: {
      return " Inc VAT";
    }
    case VatStatus.NO_VAT: {
      return advert.condition === "New" && channel === Channel.vans
        ? ""
        : " No VAT";
    }
    default: {
      return "";
    }
  }
};

const isTpfOrVan = (channel: Channel | null): boolean => {
  return (
    channel === Channel.vans ||
    channel === Channel.trucks ||
    channel === Channel.farm ||
    channel === Channel.plant
  );
};

export const getDisplayPrice = (
  advert: FPADataQuery_search_Search_advert_Advert,
  channel: Channel,
) => {
  const { isAuction, vatStatus, plusVatIndicated, priceOnApplication } = advert;

  if (isAuction) {
    return "AUCTION";
  } else if (priceOnApplication) {
    return "POA";
  }

  const price = getAdvertPrice(advert, channel);
  let displayPrice = getPriceFormatted(price);

  if (displayPrice) {
    if (vatStatus && isTpfOrVan(channel)) {
      displayPrice += getVatStatus(advert, channel);
    } else if (plusVatIndicated && channel !== Channel.cars) {
      displayPrice += " +VAT";
    }

    return displayPrice;
  }

  return;
};

export const getDisplayRrp = (
  advert: FPADataQuery_search_Search_advert_Advert,
  channel: Channel,
) => {
  const { rrp, priceOnApplication, plusVatIndicated } = advert;

  const price = getAdvertPrice(advert, channel);
  const isPriceOnApplicationLeisureAdvert =
    priceOnApplication && isTruckPlantFarm(channel);

  if (price && rrp) {
    let displayRrp = getPriceFormatted(rrp);

    if (rrp <= price || isPriceOnApplicationLeisureAdvert) {
      return;
    }

    if (displayRrp && plusVatIndicated && channel !== Channel.cars) {
      displayRrp += " +VAT";
    }

    return displayRrp;
  } else {
    return;
  }
};

export const getFormattedDate = (date: Date): string => {
  return `${date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}`;
};
